package com.ijoin.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Image
import androidx.compose.material.icons.filled.Person
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import com.ijoin.data.api.ApiClient
import com.ijoin.data.model.LegalDocument
import com.ijoin.data.model.LegalDocumentType
import com.ijoin.data.repository.AuthRepository
import com.ijoin.data.repository.LegalRepository
import com.ijoin.getPlatform
import com.ijoin.i18n.AppLanguage
import com.ijoin.i18n.LocalLocaleManager
import com.ijoin.i18n.SupportedLanguages
import com.ijoin.i18n.strings
import com.ijoin.deeplink.DeepLinkHandler
import com.ijoin.platform.redirectToUrl
import com.ijoin.platform.startOAuthFlow
import com.ijoin.platform.rememberMediaPicker
import com.ijoin.ui.components.LegalCheckbox
import com.ijoin.ui.components.LegalDocumentDialog
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAccentSoft
import com.ijoin.ui.design.EventCardAlert
import com.ijoin.ui.design.EventCardBackground
import com.ijoin.ui.design.EventCardInputCorner
import com.ijoin.ui.design.EventCardOutline
import com.ijoin.ui.design.EventOutlineButton
import com.ijoin.ui.design.EventPrimaryButton
import com.ijoin.ui.design.EventSurfaceCard
import com.ijoin.ui.design.EventTextAction
import com.ijoin.ui.design.eventBackgroundBrush
import com.ijoin.ui.design.eventOutlinedFieldColors
import com.ijoin.ui.test.TestTags
import com.ijoin.ui.util.buildAppleAuthStartUrl
import com.ijoin.ui.util.buildGoogleAuthStartUrl
import com.ijoin.analytics.AnalyticsTracker
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun RegisterScreen(
    onRegisterSuccess: () -> Unit,
    onNavigateToLogin: () -> Unit,
    authRepository: AuthRepository = remember { AuthRepository() },
    legalRepository: LegalRepository = remember { LegalRepository() }
) {
    val strings = strings()
    val localeManager = LocalLocaleManager.current
    val scope = rememberCoroutineScope()
    val platform = remember { getPlatform() }

    var email by remember { mutableStateOf("") }
    var username by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    var firstName by remember { mutableStateOf("") }
    var lastName by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }
    var avatarPreview by remember { mutableStateOf<String?>(null) }
    var selectedLanguage by remember { mutableStateOf(localeManager.currentLanguage) }
    var languageDropdownExpanded by remember { mutableStateOf(false) }

    var passwordVisible by remember { mutableStateOf(false) }
    var confirmPasswordVisible by remember { mutableStateOf(false) }
    var isLoading by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    // Legal acceptance state
    var legalAccepted by remember { mutableStateOf(false) }
    var showDocumentDialog by remember { mutableStateOf<LegalDocumentType?>(null) }
    var currentDocument by remember { mutableStateOf<LegalDocument?>(null) }
    var isLoadingDocument by remember { mutableStateOf(false) }
    var documentError by remember { mutableStateOf<String?>(null) }
    var legalVersions by remember { mutableStateOf<Map<String, String>>(emptyMap()) }

    // Load legal document versions on init
    LaunchedEffect(Unit) {
        legalRepository.getCurrentVersions()
            .onSuccess { versions -> legalVersions = versions }
    }

    val avatarPicker = rememberMediaPicker()

    LaunchedEffect(avatarPicker.selectedFiles) {
        val dataUrl = avatarPicker.selectedFiles.firstOrNull { it.startsWith("data:image") }
        avatarPreview = dataUrl
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(strings.register) },
                navigationIcon = {
                    IconButton(onClick = onNavigateToLogin) {
                        Icon(Icons.Filled.ArrowBack, contentDescription = strings.back)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = EventCardBackground,
                    titleContentColor = EventCardAccentDark,
                    navigationIconContentColor = EventCardAccentDark
                )
            )
        },
        containerColor = EventCardBackground
    ) { paddingValues ->
        val focusManager = LocalFocusManager.current
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(eventBackgroundBrush())
                .padding(paddingValues)
                .padding(16.dp)
                .pointerInput(Unit) {
                    detectTapGestures(onTap = { focusManager.clearFocus() })
                },
            contentAlignment = Alignment.TopCenter
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .widthIn(max = 420.dp)
                    .verticalScroll(rememberScrollState()),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                EventSurfaceCard(
                    modifier = Modifier.fillMaxWidth(),
                    contentPadding = PaddingValues(24.dp)
                ) {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        Text(
                            text = strings.createAccount,
                            style = MaterialTheme.typography.headlineMedium,
                            textAlign = TextAlign.Center
                        )

                        // Legal acceptance checkbox
                        LegalCheckbox(
                            checked = legalAccepted,
                            onCheckedChange = { legalAccepted = it },
                            onDocumentClick = { type ->
                                showDocumentDialog = type
                                isLoadingDocument = true
                                documentError = null
                                currentDocument = null
                                scope.launch {
                                    legalRepository.getDocument(type)
                                        .onSuccess { doc ->
                                            currentDocument = doc
                                            isLoadingDocument = false
                                        }
                                        .onFailure { error ->
                                            documentError = error.message ?: strings.legalDocumentLoadError
                                            isLoadingDocument = false
                                        }
                                }
                            },
                            enabled = !isLoading
                        )

                        EventOutlineButton(
                            text = strings.continueWithGoogle,
                            onClick = {
                                if (!legalAccepted) {
                                    errorMessage = strings.pleaseAcceptTerms
                                    return@EventOutlineButton
                                }
                                AnalyticsTracker.trackCompleteRegistration("google")
                                val url = buildGoogleAuthStartUrl(ApiClient.getBaseUrl(), platform.isMobile)
                                if (platform.isMobile) {
                                    startOAuthFlow(url, "ijoin") { callbackUrl ->
                                        callbackUrl?.let { DeepLinkHandler.handleUrl(it) }
                                    }
                                } else {
                                    redirectToUrl(url)
                                }
                            },
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(50.dp)
                                .testTag(TestTags.RegisterScreen.GOOGLE_BUTTON),
                            enabled = !isLoading && legalAccepted
                        )

                        EventOutlineButton(
                            text = strings.continueWithApple,
                            onClick = {
                                if (!legalAccepted) {
                                    errorMessage = strings.pleaseAcceptTerms
                                    return@EventOutlineButton
                                }
                                AnalyticsTracker.trackCompleteRegistration("apple")
                                val url = buildAppleAuthStartUrl(ApiClient.getBaseUrl(), platform.isMobile)
                                if (platform.isMobile) {
                                    startOAuthFlow(url, "ijoin") { callbackUrl ->
                                        callbackUrl?.let { DeepLinkHandler.handleUrl(it) }
                                    }
                                } else {
                                    redirectToUrl(url)
                                }
                            },
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(50.dp)
                                .testTag(TestTags.RegisterScreen.APPLE_BUTTON),
                            enabled = !isLoading && legalAccepted
                        )

                        ProfilePhotoPicker(
                            avatarPreview = avatarPreview,
                            onPickPhoto = {
                                errorMessage = null
                                avatarPicker.pickFiles()
                            },
                            onRemovePhoto = { avatarPreview = null },
                            isLoading = isLoading,
                            strings = strings
                        )

                        OutlinedTextField(
                            value = email,
                            onValueChange = {
                                email = it
                                errorMessage = null
                            },
                            label = { Text(strings.email) },
                            singleLine = true,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                            modifier = Modifier
                                .fillMaxWidth()
                                .testTag(TestTags.RegisterScreen.EMAIL_FIELD),
                            enabled = !isLoading,
                            shape = RoundedCornerShape(EventCardInputCorner),
                            colors = eventOutlinedFieldColors()
                        )

                        OutlinedTextField(
                            value = username,
                            onValueChange = {
                                username = it
                                errorMessage = null
                            },
                            label = { Text(strings.username) },
                            singleLine = true,
                            modifier = Modifier
                                .fillMaxWidth()
                                .testTag(TestTags.RegisterScreen.USERNAME_FIELD),
                            enabled = !isLoading,
                            shape = RoundedCornerShape(EventCardInputCorner),
                            colors = eventOutlinedFieldColors()
                        )

                        OutlinedTextField(
                            value = password,
                            onValueChange = {
                                password = it
                                errorMessage = null
                            },
                            label = { Text(strings.password) },
                            singleLine = true,
                            visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                            trailingIcon = {
                                IconButton(onClick = { passwordVisible = !passwordVisible }) {
                                    Icon(
                                        imageVector = if (passwordVisible) Icons.Filled.Visibility else Icons.Filled.VisibilityOff,
                                        contentDescription = if (passwordVisible) "Hide password" else "Show password"
                                    )
                                }
                            },
                            modifier = Modifier
                                .fillMaxWidth()
                                .testTag(TestTags.RegisterScreen.PASSWORD_FIELD),
                            enabled = !isLoading,
                            shape = RoundedCornerShape(EventCardInputCorner),
                            colors = eventOutlinedFieldColors()
                        )

                        OutlinedTextField(
                            value = confirmPassword,
                            onValueChange = {
                                confirmPassword = it
                                errorMessage = null
                            },
                            label = { Text(strings.confirmPassword) },
                            singleLine = true,
                            visualTransformation = if (confirmPasswordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                            trailingIcon = {
                                IconButton(onClick = { confirmPasswordVisible = !confirmPasswordVisible }) {
                                    Icon(
                                        imageVector = if (confirmPasswordVisible) Icons.Filled.Visibility else Icons.Filled.VisibilityOff,
                                        contentDescription = if (confirmPasswordVisible) "Hide password" else "Show password"
                                    )
                                }
                            },
                            modifier = Modifier
                                .fillMaxWidth()
                                .testTag(TestTags.RegisterScreen.CONFIRM_PASSWORD_FIELD),
                            enabled = !isLoading,
                            shape = RoundedCornerShape(EventCardInputCorner),
                            colors = eventOutlinedFieldColors()
                        )

                        Divider(color = EventCardOutline)

                        Text(
                            text = strings.optional,
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )

                        OutlinedTextField(
                            value = firstName,
                            onValueChange = { firstName = it },
                            label = { Text(strings.firstName) },
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth(),
                            enabled = !isLoading,
                            shape = RoundedCornerShape(EventCardInputCorner),
                            colors = eventOutlinedFieldColors()
                        )

                        OutlinedTextField(
                            value = lastName,
                            onValueChange = { lastName = it },
                            label = { Text(strings.lastName) },
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth(),
                            enabled = !isLoading,
                            shape = RoundedCornerShape(EventCardInputCorner),
                            colors = eventOutlinedFieldColors()
                        )

                        OutlinedTextField(
                            value = phone,
                            onValueChange = { phone = it },
                            label = { Text(strings.phone) },
                            singleLine = true,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                            modifier = Modifier.fillMaxWidth(),
                            enabled = !isLoading,
                            shape = RoundedCornerShape(EventCardInputCorner),
                            colors = eventOutlinedFieldColors()
                        )

                        // Language selector
                        ExposedDropdownMenuBox(
                            expanded = languageDropdownExpanded,
                            onExpandedChange = { if (!isLoading) languageDropdownExpanded = it }
                        ) {
                            OutlinedTextField(
                                value = selectedLanguage.nativeName,
                                onValueChange = {},
                                readOnly = true,
                                label = { Text(strings.language) },
                                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = languageDropdownExpanded) },
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .menuAnchor(),
                                enabled = !isLoading,
                                shape = RoundedCornerShape(EventCardInputCorner),
                                colors = eventOutlinedFieldColors()
                            )
                            ExposedDropdownMenu(
                                expanded = languageDropdownExpanded,
                                onDismissRequest = { languageDropdownExpanded = false }
                            ) {
                                SupportedLanguages.all.forEach { language ->
                                    DropdownMenuItem(
                                        text = { Text(language.nativeName) },
                                        onClick = {
                                            selectedLanguage = language
                                            languageDropdownExpanded = false
                                        }
                                    )
                                }
                            }
                        }

                        if (errorMessage != null) {
                            Text(
                                text = errorMessage.orEmpty(),
                                color = EventCardAlert,
                                style = MaterialTheme.typography.bodySmall,
                                textAlign = TextAlign.Center
                            )
                        }

                        EventPrimaryButton(
                            text = strings.register,
                            onClick = {
                                // Validate required fields
                                if (email.isBlank() || username.isBlank() || password.isBlank() || confirmPassword.isBlank()) {
                                    errorMessage = strings.fillAllFields
                                    return@EventPrimaryButton
                                }

                                if (!email.contains("@")) {
                                    errorMessage = strings.invalidEmail
                                    return@EventPrimaryButton
                                }

                                if (password != confirmPassword) {
                                    errorMessage = strings.passwordsDontMatch
                                    return@EventPrimaryButton
                                }

                                if (!legalAccepted) {
                                    errorMessage = strings.pleaseAcceptTerms
                                    return@EventPrimaryButton
                                }

                                isLoading = true
                                scope.launch {
                                    authRepository.register(
                                          email = email,
                                          username = username,
                                          password = password,
                                          firstName = firstName.ifBlank { null },
                                          lastName = lastName.ifBlank { null },
                                          phone = phone.ifBlank { null },
                                          avatarDataUrl = avatarPreview,
                                          language = selectedLanguage.code,
                                          legalAccepted = true,
                                          acceptedVersions = legalVersions.ifEmpty { null }
                                      )
                                        .onSuccess {
                                            isLoading = false
                                            AnalyticsTracker.trackCompleteRegistration("email")
                                            onRegisterSuccess()
                                        }
                                        .onFailure { error ->
                                            isLoading = false
                                            errorMessage = error.message ?: strings.registerError
                                        }
                                }
                            },
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(50.dp)
                                .testTag(TestTags.RegisterScreen.SUBMIT_BUTTON),
                            enabled = !isLoading && legalAccepted,
                            isLoading = isLoading
                        )

                        Row(
                            horizontalArrangement = Arrangement.Center,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = strings.alreadyHaveAccount,
                                style = MaterialTheme.typography.bodyMedium
                            )
                            Spacer(modifier = Modifier.width(4.dp))
                            EventTextAction(
                                text = strings.loginHere,
                                onClick = onNavigateToLogin
                            )
                        }
                    }
                }

                Spacer(modifier = Modifier.height(16.dp))
            }
        }
    }

    // Legal document dialog
    if (showDocumentDialog != null) {
        LegalDocumentDialog(
            document = currentDocument,
            isLoading = isLoadingDocument,
            error = documentError,
            onDismiss = {
                showDocumentDialog = null
                currentDocument = null
                documentError = null
            }
        )
    }
}

@Composable
private fun ProfilePhotoPicker(
    avatarPreview: String?,
    onPickPhoto: () -> Unit,
    onRemovePhoto: () -> Unit,
    isLoading: Boolean,
    strings: com.ijoin.i18n.Strings
) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Text(
            text = "${strings.profile} photo (${strings.optional})",
            style = MaterialTheme.typography.labelMedium
        )
        val hasPhoto = !avatarPreview.isNullOrBlank()
        Box(
            modifier = Modifier
                .size(96.dp)
                .clip(CircleShape)
                .background(EventCardAccentSoft),
            contentAlignment = Alignment.Center
        ) {
            if (hasPhoto) {
                AsyncImage(
                    model = avatarPreview,
                    contentDescription = null,
                    modifier = Modifier.fillMaxSize()
                )
            } else {
                Icon(
                    imageVector = Icons.Filled.Person,
                    contentDescription = null,
                    tint = EventCardAccentDark
                )
            }
        }
        EventOutlineButton(
            text = strings.addMedia,
            onClick = onPickPhoto,
            enabled = !isLoading,
            leadingIcon = Icons.Filled.Image
        )
        if (hasPhoto) {
            EventTextAction(
                text = strings.delete,
                onClick = onRemovePhoto,
                enabled = !isLoading,
                contentColor = EventCardAlert
            )
        }
    }
}
